// @flow

import React, { useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { useMutation } from '@apollo/react-hooks';
import { useHistory } from 'react-router-dom';
import startsWith from 'lodash/startsWith';
import { Controller, useForm } from 'react-hook-form';
import { isValidNumber, parseNumber } from 'libphonenumber-js';

import usePath from '../../usePath';
import useSelfEnrollment from '../../../../hooks/useSelfEnrollment';
import StandardButton from '../../../../components/Buttons/Standard';
import PhonePrefixInput from './PhonePrefixInput';
import Label from '../../IdentifyPatient/Form/Label';

import { SELF_ENROLLMENT } from '../../mutations';
import { MESSAGES, PATHS } from '../../constants';

import styles from './styles.module.scss';

const PhoneForm = () => {
  const getPath = usePath();
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { formData, setMessage, setSelfEnrollmentResponse } = useSelfEnrollment();

  // Prevent sensitive data exposure in cache.
  const [selfEnrollment, { loading }] = useMutation(SELF_ENROLLMENT, { fetchPolicy: 'no-cache' });

  const {
    handleSubmit,
    control,
    errors,
    watch,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      phone: '',
    },
  });

  const phoneValue = watch('phone');

  const handleSubmitForm = async ({ phone }) => {
    const phoneNumber = !startsWith(phone, '+') ? `+${phone}` : phone;
    const input = { ...formData, phoneNumber };

    const response = await selfEnrollment({ variables: { input } });

    const selfEnrollmentResponse = response?.data?.selfEnrollPatient || null;
    setSelfEnrollmentResponse(selfEnrollmentResponse);

    if (selfEnrollmentResponse?.success) {
      setMessage(MESSAGES.THANK_YOU);
      history.push(getPath(PATHS.ENROLLED));
    } else {
      history.push(getPath(PATHS.IDENTIFY));
    }
  };

  const submitOnEnter = (event) => {
    if (event.key === 'Enter' && isValid && !loading && phoneValue) {
      handleSubmit(handleSubmitForm)();
      event.preventDefault();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', submitOnEnter);
    return () => {
      window.removeEventListener('keydown', submitOnEnter);
    };
  });

  const submitDisabled = !isValid || loading || !phoneValue;

  return (
    <form className={styles.phoneFormContainer}>
      <div className={styles.inputContainer}>
        <Label text={formatMessage({ id: 'app.forms.phone.number' })} required />
        <Controller
          name="phone"
          control={control}
          rules={{
            validate: (value) => {
              if (!value) return true;
              const parsePhoneNumber = parseNumber(`+${value}`);
              const { country } = parsePhoneNumber;
              const args = [value.startsWith('+') ? value : `+${value}`, country];
              return isValidNumber(...args);
            },
          }}
          render={({ name, value, onChange }) => (
            <PhonePrefixInput
              country="us"
              input={{ name, value, onChange }}
              disabled={loading}
              placeholder={formatMessage({ id: 'app.forms.phone.enter.mobile.number' })}
              meta={{ touched: !!errors[name], error: !!errors[name] }}
            />
          )}
          data-testid="manage-user-phone"
        />
      </div>

      <div className={styles.disclaimer}>
        <FormattedMessage id="app.self-enroll.found.text-rate-disclaimer" />
      </div>

      <StandardButton
        disabled={submitDisabled}
        extraRootClasses={classnames(styles.button, { [styles.buttonDisabled]: submitDisabled })}
        isPill
        variant="primary"
        onClick={handleSubmit(handleSubmitForm)}
      >
        <FormattedMessage id="app.self-enroll.found.submit" />
      </StandardButton>
    </form>
  );
};

export default PhoneForm;
